import {
  getHourInTimezone,
  getMinuteInTimezone,
} from '@/lib/scheduling/timezone';

export type TimeFormat = '12h' | '24h';

// Must equal the Django Org.default_time_format default so existing orgs and
// the no-currentOrg case render identically (no flicker, correct mid blue-green deploy).
export const DEFAULT_TIME_FORMAT: TimeFormat = '24h';

export const TIME_FORMAT_OPTIONS = [
  { value: '24h', label: '24-hour (14:30)' },
  { value: '12h', label: '12-hour (2:30 PM)' },
] as const;

export function isHour12(format: TimeFormat): boolean {
  return format === '12h';
}

export function hourCycleFor(format: TimeFormat): 12 | 24 {
  return format === '12h' ? 12 : 24;
}

export function timeParts(format: TimeFormat): {
  hour: 'numeric' | '2-digit';
  minute: '2-digit';
  hourCycle: 'h12' | 'h23';
} {
  return {
    hour: format === '12h' ? 'numeric' : '2-digit',
    minute: '2-digit',
    // Use an explicit hour cycle instead of `hour12`. In V8, `hour12: false`
    // can render the first hour after midnight as `24:30` instead of `00:30`;
    // `hourCycle: 'h23'` forces the correct 00–23 cycle. `'h12'` keeps AM/PM.
    hourCycle: format === '12h' ? 'h12' : 'h23',
  };
}

/**
 * Format `date` with Intl, applying `timeZone` when valid and falling back to
 * browser-local time when it isn't. The single home for the invalid-timezone
 * try/catch — do not reimplement this fallback at call sites.
 */
export function formatWithSafeTimeZone(
  date: Date,
  options: Intl.DateTimeFormatOptions,
  extra?: { locale?: string; timeZone?: string }
): string {
  try {
    return new Intl.DateTimeFormat(extra?.locale, {
      ...options,
      ...(extra?.timeZone ? { timeZone: extra.timeZone } : {}),
    }).format(date);
  } catch {
    // Invalid timeZone — fall back to browser local time
    return new Intl.DateTimeFormat(extra?.locale, options).format(date);
  }
}

/**
 * Render a 12-hour time with the on-the-hour minute segment removed
 * ("9:00 AM" → "9 AM"). Locale-safe: instead of a `':00'` string match — which
 * only works for ASCII digits and a literal ":" separator — it drops the
 * `minute` Intl field entirely when the minute is zero, letting `Intl` render
 * "9 AM" directly. The zero check reads the minute through a fixed `en-US`
 * numeric formatter so it is independent of the display locale's numbering
 * system (e.g. Arabic-Indic "٠٠"), and keeps `timeZone` so offsets with
 * non-zero minutes (e.g. Asia/Kolkata) are respected.
 */
function formatOmittingZeroMinutes(
  date: Date,
  options: Intl.DateTimeFormatOptions,
  extra?: { locale?: string; timeZone?: string }
): string {
  const minuteIsZero =
    formatWithSafeTimeZone(
      date,
      { minute: 'numeric' },
      { locale: 'en-US', timeZone: extra?.timeZone }
    ) === '0';
  if (!minuteIsZero) {
    return formatWithSafeTimeZone(date, options, extra);
  }
  // Omitting the `minute` field is what makes this locale-safe: `.format()`
  // spaces the AM/PM marker with the same character it uses everywhere else,
  // so no whitespace/numbering assumptions leak in via string surgery.
  const { minute: _minute, ...withoutMinute } = options;
  return formatWithSafeTimeZone(date, withoutMinute, extra);
}

export interface FormatTimeOptions {
  /** IANA timezone (e.g. "America/New_York"); invalid values fall back to browser-local. */
  timeZone?: string;
  /** BCP-47 locale passed to Intl; defaults to the browser locale. */
  locale?: string;
  /**
   * Extra Intl date fields rendered before the time, e.g.
   * `{ month: 'short', day: 'numeric' }` → "Jul 15, 9:00 AM".
   */
  dateParts?: Intl.DateTimeFormatOptions;
  /**
   * 12-hour only: render on-the-hour times without ":00" ("9 AM" instead of
   * "9:00 AM"). 24-hour output always keeps minutes ("14:00") since ":00" is a
   * genuine minute value there.
   */
  omitZeroMinutes?: boolean;
}

/**
 * The canonical time-of-day formatter. Every user-facing time label should
 * flow through here (directly or via a thin domain wrapper) so the org
 * time-format decision, the midnight-safe hour cycle, the invalid-timezone
 * fallback, and the ":00"-stripping rule each live in exactly one place.
 */
export function formatTime(
  value: string | Date,
  format: TimeFormat,
  options?: FormatTimeOptions
): string {
  // `new Date(...)` rather than a `typeof` branch so out-of-contract runtime
  // values (null, undefined, numbers from untyped API data) degrade the same
  // way they do in the Date constructor instead of throwing.
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const intlOptions = { ...(options?.dateParts ?? {}), ...timeParts(format) };
  const extra = { locale: options?.locale, timeZone: options?.timeZone };
  // Intl always includes minutes (e.g. "9:00 AM"). Drop the on-the-hour minute
  // segment on request — a 12-hour-only convenience; see
  // FormatTimeOptions.omitZeroMinutes.
  if (options?.omitZeroMinutes && isHour12(format)) {
    return formatOmittingZeroMinutes(date, intlOptions, extra);
  }
  return formatWithSafeTimeZone(date, intlOptions, extra);
}

/**
 * Compact Google-Calendar-style time label for dense surfaces (month-view
 * event rows, chips):
 *
 * - 12-hour: "8am", "2pm", "4:30pm" — drops the minute segment when it's :00
 *   and lowercases the am/pm marker.
 * - 24-hour: "8:00", "14:00", "14:30" — keeps the minute segment (a bare hour
 *   like "14" reads as a date), no am/pm marker.
 *
 * Renders in browser-local time. Returns '' for unparseable input.
 */
export function formatTimeCompact(
  value: string | Date,
  format: TimeFormat,
  timeZone?: string
): string {
  // See formatTime for why this avoids a `typeof` branch.
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  // Pass `timeZone` to render the wall clock of an explicit zone (every
  // authenticated surface should — see FRONTEND_PATTERNS.md § Timezones).
  // Omitting it keeps the legacy browser-local reading for the call sites that
  // have not been threaded yet.
  const hours24 = timeZone
    ? getHourInTimezone(date, timeZone)
    : date.getHours();
  const minutes = timeZone
    ? getMinuteInTimezone(date, timeZone)
    : date.getMinutes();
  if (format === '24h') {
    return `${hours24}:${minutes.toString().padStart(2, '0')}`;
  }
  const hour12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const ampm = hours24 < 12 ? 'am' : 'pm';
  if (minutes === 0) {
    return `${hour12}${ampm}`;
  }
  return `${hour12}:${minutes.toString().padStart(2, '0')}${ampm}`;
}
